import { selectIsThemeModeEnabled } from "@/store/theme/theme-slice";
import { useAppSelector } from "@/store/store-hooks";
import { FaroButton, FaroText } from "@faro-lotv/flat-ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import type { JSX } from "react";
import { ThemeModeToggle } from "./theme-mode-toggle";

type DevToolsTableProps = {
  isResetting: boolean;
  onResetFueFlags(): void;
  searchText: string;
};

/** @returns Developer-only actions for experimental features. */
export function DevToolsTable({
  isResetting,
  onResetFueFlags,
  searchText,
}: DevToolsTableProps): JSX.Element {
  const isThemeModeEnabled = useAppSelector(selectIsThemeModeEnabled);

  const devTools = [
    {
      label: "Reset feature tour flags",
      description: "Reset the feature tour flags for the current user.",
      control: (
        <FaroButton size="s" disabled={isResetting} onClick={onResetFueFlags}>
          {isResetting ? "Resetting..." : "Reset"}
        </FaroButton>
      ),
    },
    ...(isThemeModeEnabled
      ? [
          {
            label: "Theme mode",
            description:
              "Light, dark or system theme. Shared with the Dashboard user setting. Dark mode is work in progress.",
            control: <ThemeModeToggle />,
          },
        ]
      : []),
  ];

  const normalizedSearchText = searchText.toLowerCase();
  const visibleDevTools = devTools.filter(({ label, description }) =>
    [label, description].some((text) =>
      text.toLowerCase().includes(normalizedSearchText),
    ),
  );

  return (
    <Table sx={{ width: "100%" }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ p: 1 }}>
            <FaroText variant="helpText">Action</FaroText>
          </TableCell>
          <TableCell sx={{ p: 1 }}>
            <FaroText variant="helpText">Description</FaroText>
          </TableCell>
          <TableCell sx={{ p: 1 }} />
        </TableRow>
      </TableHead>
      <TableBody>
        {visibleDevTools.map(({ label, description, control }) => (
          <TableRow key={label}>
            <TableCell sx={{ p: 1 }}>
              <FaroText variant="heading12">{label}</FaroText>
            </TableCell>
            <TableCell sx={{ p: 1 }}>
              <FaroText variant="helpText">{description}</FaroText>
            </TableCell>
            <TableCell align="right" sx={{ p: 1 }}>
              {control}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
